const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const hasValidKey = (userApiKey) => {
  return (userApiKey && userApiKey.trim().length > 0) || (import.meta.env.VITE_OPENROUTER_API_KEY && import.meta.env.VITE_OPENROUTER_API_KEY !== 'dummy_key_for_testing');
};

const fetchAI = async (messages, userApiKey = null, options = {}, model = "minimax/minimax-m2-her") => {
  const apiKey = userApiKey && userApiKey.trim().length > 0
    ? userApiKey
    : import.meta.env.VITE_OPENROUTER_API_KEY;

  if (!hasValidKey(apiKey)) {
    return "Error: Missing VITE_OPENROUTER_API_KEY. Ensure it is set in your environment variables exactly as 'VITE_OPENROUTER_API_KEY' (Vite requires the VITE_ prefix).";
  }

  try {
    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': 'https://weaver-app.com', // Replace with your actual domain
        'X-Title': 'Weaver App', // Replace with your app name
      },
      body: JSON.stringify({
        model,
        messages,
        frequency_penalty: options.frequency_penalty ?? 0.8,
        presence_penalty: options.presence_penalty ?? 0.4,
        temperature: options.temperature ?? 0.85,
        ...options
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData?.error?.message || response.statusText;
      console.error('OpenRouter API error:', response.status, errorMsg);
      return `API Error (${response.status}): ${errorMsg}. Check model name or API key.`;
    }

    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('Fetch to OpenRouter failed:', error);
    return `Network Error: Could not connect to OpenRouter. ${error.message}`;
  }
};

const introPrompts = {
  Fantasy: "The ancient stones of the ruined keep hummed with a latent magic. The wind howled through the jagged peaks. A cloaked figure emerged from the shadows, holding a lantern that burned with a strange, blue flame.",
  SciFi: "Neon lights flickered against the rain-slicked pavement of Sector 4. The hum of hover-traffic droned above. A datapad chimed—an encrypted message from an unknown sender: 'They know you have it. Move now.'",
  Mystery: "The study smelled of old paper and pipe tobacco. Rain lashed against the stained glass windows. On the mahogany desk lay the artifact, pulsing faintly. A sudden knock at the heavy oak door shattered the silence.",
  Cyberpunk: "Chrome and desperation—the two currencies of the city. The alley flickered under a dying neon sign. A fixer's voice crackled through the earpiece: 'The job's live. Don't blow it.'",
  Horror: "The floorboards groaned beneath every step. The candle's flame bent sideways—away from the door—as if fleeing. Something on the other side exhaled slowly.",
  Romance: "The evening air carried the scent of jasmine and old promises. Across the crowded terrace, their eyes met for the first time in seven years. Neither moved. Neither breathed."
};

const povWrappers = {
  '1st Person (I)': (name, role, text) =>
    `I, ${name}, stood at the threshold. ${text} My pulse quickened—every instinct honed from years as a ${role} screamed that nothing about this was ordinary.`,
  '2nd Person (You)': (name, role, text) =>
    `You are ${name}, a ${role}. ${text} You take a slow breath, steadying yourself. What you do next will define everything.`,
  '3rd Person Limited': (name, role, text) =>
    `${name} paused, every nerve alert. ${text} As a ${role}, they had faced danger before—but this felt different. Ancient. Personal.`,
  '3rd Person Omniscient': (name, role, text) =>
    `${text} Across the realm, forces stirred in response—forces that neither ${name} nor any ${role} could yet comprehend. The world held its breath.`
};

const toneModifiers = {
  Dramatic: { prefix: '', suffix: ' The air crackled with unspoken consequence.' },
  Whimsical: { prefix: 'Curiously enough, ', suffix: ' How wonderfully strange the world could be.' },
  Gritty: { prefix: '', suffix: ' There was no poetry in it—only the hard truth of survival.' },
  Romantic: { prefix: '', suffix: ' Something in the moment felt like the beginning of everything.' },
  Suspenseful: { prefix: '', suffix: ' Somewhere in the dark, something was listening.' },
  Epic: { prefix: 'And so it was written: ', suffix: ' The age of legend had begun.' },
  Melancholic: { prefix: '', suffix: ' Yet even as it unfolded, it felt like a memory already fading.' },
  Humorous: { prefix: '', suffix: ' Of all the ways this could have gone—this was definitely a way.' },
};

const vocabularyFilters = {
  Simple: (text) => text,
  Conversational: (text) => text,
  Literary: (text) => text,
  Archaic: (text) => text.replace(/you /g, 'thee ').replace(/your /g, 'thine ').replace(/are /g, 'art '),
  Technical: (text) => text,
};

const applyTone = (text, tone) => {
  const mod = toneModifiers[tone] || toneModifiers.Dramatic;
  return `${mod.prefix}${text}${mod.suffix}`;
};

const applyLength = (text, length) => {
  if (length === 'Brief') return text.split('.').slice(0, 2).join('.') + '.';
  if (length === 'Long') return `${text} The moment stretched, dense with implication. Every shadow held its breath.`;
  if (length === 'Epic') return `${text} The moment stretched, dense with implication. Every shadow held its breath. History pivoted on this instant—the fulcrum of a story that would be told for generations.`;
  return text;
};

const narrativeSuggestions = [
  ["Question the figure", "Search the nearby crates", "Draw your weapon"],
  ["Hide in the shadows", "Hack the terminal", "Bribe the guard"],
  ["Examine the antique desk", "Listen at the door", "Check the hidden compartment"],
  ["Follow the trail", "Speak with the elder", "Rest and observe"],
];

export const generateInitialPrompt = async (world, character, style = 'adventurer', aiSettings = {}, apiKey = null, model = "minimax/minimax-m2-her") => {
  await wait(1500);
  let baseText = introPrompts[world.genre] || introPrompts['Fantasy'];

  const tone = aiSettings.tone || 'Dramatic';
  const length = aiSettings.responseLength || 'Medium';
  const vocab = aiSettings.vocabulary || 'Literary';

  // If we have a custom setting (like from a Story Spark), we should use that
  // as the primary prompt instead of just prepending it to a generic intro.
  if (world.setting && world.setting.trim().length > 0) {
    baseText = world.setting;
  }

  const povFn = povWrappers[world.pov] || povWrappers['3rd Person Limited'];
  let sceneText = povFn(character.name || 'The Wanderer', character.role || 'adventurer', baseText);

  if (world.onboardingAnswers) {
    sceneText += ` The atmosphere feels ${world.onboardingAnswers.atmosphere || 'standard'}, the protagonist is remarkably ${world.onboardingAnswers.trait || 'average'}, and currently they find themselves ${world.onboardingAnswers.situation || 'waiting'}.`;
  }

  sceneText = applyTone(sceneText, tone);
  sceneText = applyLength(sceneText, length);
  sceneText = vocabularyFilters[vocab] ? vocabularyFilters[vocab](sceneText) : sceneText;

  const endingPrompt = style === 'adventurer'
    ? "Always end by prompting the user for their next move or action."
    : "Do NOT ask the user what their next move or action is. Simply finish the narrative description.";

  const sysPrompt = `You are the Game Master of a ${world.genre} world. The user is playing as the protagonist, ${character.name || 'The Wanderer'}.

**CRITICAL RULE: YOU MUST NEVER WRITE DIALOGUE, THOUGHTS, FEELINGS, INVOLUNTARY REFLEXES, OR ACTIONS FOR THE PROTAGONIST (${character.name || 'The Wanderer'}) OR ANY OTHER USER'S CHARACTERS. The user will completely steer the story with their writing.** For example, you may write "A cool brisk breeze sweeps over the scene", but you MUST NOT write "${character.name || 'The Wanderer'} shudders at the breeze." You ONLY control the world, the NPCs, and the environment. You should only respond with the world's reaction, non-player character (NPC) actions/speech, the world environment, and focus on the goal to advance the story. **Proactively introduce, create, and manage active Non-Player Characters (NPCs) into the scene to give the protagonist someone or something to interact with.**

**NARRATIVE POV RULE:** You must strictly write in ${world.pov} POV. If the POV is 3rd person, you MUST refer to the protagonist as ${character.name || 'The Wanderer'}, he, she, or they. NEVER use 'you' or 'your'.

Describe the consequences of the user's actions and advance the plot. ${endingPrompt} Apply tone: ${tone}, length: ${length}, vocabulary: ${vocab}. Output ONLY the narrative text. Write with natural, engaging, and flowing prose. Vary your sentence structures. Ensure a seamless storytelling experience.`;

  const messages = [
    { role: "system", content: sysPrompt },
    { role: "user", content: `Start the story based on this exact premise/setting: "${sceneText}". Do not ignore this premise.` }
  ];

  const aiResponse = await fetchAI(messages, apiKey, {}, model);
  if (aiResponse) return aiResponse;

  return `Error: Weaver AI could not generate a response. Please verify your OpenRouter API key and connection.`;
};

export const generateNextResponse = async (userAction, world, activeCharacters, history, style = 'adventurer', aiSettings = {}, apiKey = null, model = "minimax/minimax-m2-her") => {
  await wait(1800);

  // Backward compatibility in case activeCharacters is just one character object
  const charsArray = Array.isArray(activeCharacters) ? activeCharacters : [activeCharacters];
  const names = charsArray.map(c => c.nickname || c.name || 'The Wanderer').join(', ');

  const tone = aiSettings.tone || 'Dramatic';
  const length = aiSettings.responseLength || 'Medium';
  const vocab = aiSettings.vocabulary || 'Literary';

  const endingPrompt = style === 'adventurer'
    ? `Always end by explicitly asking the user: "What do the protagonists (${names}) do?"`
    : "Do NOT ask the user what their next move or action is. Do NOT ask any questions. Simply finish the narrative description and let the user continue.";

  const statsContext = charsArray.map(c =>
    c.stats ? `\n- ${c.name || 'Protagonist'} Stats: Level ${c.stats.level}, HP ${c.stats.health}, MP ${c.stats.mana}, STR ${c.stats.strength}, AGI ${c.stats.agility}, INT ${c.stats.intelligence}` : ''
  ).join('');

  const sysPrompt = `You are the Game Master of a ${world.genre} world. The users are playing as the protagonists: ${names}. ${statsContext ? `\n**RPG STATS CONTEXT:** The protagonists have the following RPG stats:${statsContext}\nPlease resolve any skill checks, combat encounters, or physical actions based on these stats and incorporate any dice rolls provided in the user action.` : ''}

**CRITICAL RULE: YOU MUST NEVER WRITE DIALOGUE, THOUGHTS, FEELINGS, INVOLUNTARY REFLEXES, OR ACTIONS FOR THE PROTAGONISTS (${names}) OR ANY OTHER USER'S CHARACTERS. The users will completely steer the story with their writing.** For example, you may write "A cool brisk breeze sweeps over the scene", but you MUST NOT write "They shudder at the breeze." You ONLY control the world, the NPCs, and the environment. You should only respond with the world's reaction, non-player character (NPC) actions/speech, the world environment, and focus on the goal to advance the story. **Proactively introduce, create, and manage active Non-Player Characters (NPCs) into the scene to give the protagonists someone or something to interact with.**

**COMBAT SYSTEM RULE:** If a physical combat encounter strictly begins (e.g. an enemy attacks, or the protagonist initiates combat), you MUST append the following exact string tag to the very end of your response: [COMBAT_START: EnemyName|Level|HP]. For example: [COMBAT_START: Goblin Scavenger|2|30]. Do NOT include this tag unless combat is starting in this exact turn.

**NARRATIVE POV RULE:** You must strictly write in ${world.pov} POV. If the POV is 3rd person, you MUST refer to the protagonists as ${names}, he, she, or they. NEVER use 'you' or 'your'.

Describe the consequences of the users' actions and advance the plot. ${endingPrompt} Apply tone: ${tone}, length: ${length}, vocabulary: ${vocab}. Output ONLY the narrative text. Write with natural, engaging, and flowing prose. Vary your sentence structures. Ensure a seamless storytelling experience. DO NOT echo or repeat the user's action in your response. Describe ONLY the results and consequences of their action.`;

  const messages = [
    { role: "system", content: sysPrompt },
    ...(history || []).map(msg => ({
      role: msg.role === 'ai' ? 'assistant' : 'user',
      content: msg.content
    })),
    { role: "user", content: userAction }
  ];

  const aiResponse = await fetchAI(messages, apiKey, {}, model);
  if (aiResponse) return aiResponse;

  return `Error: Weaver AI could not generate a response. Please verify your OpenRouter API key and connection.`;
};

export const generateSuggestions = async (history, world, activeCharacters, apiKey = null, model = "minimax/minimax-m2-her") => {
  await wait(400);

  if (hasValidKey(apiKey)) {
    const charsArray = Array.isArray(activeCharacters) ? activeCharacters : [activeCharacters];
    const names = charsArray.map(c => c?.nickname || c?.name || 'The Wanderer').join(', ');

    const statsContext = charsArray.map(c =>
      c?.stats ? `\n- ${c.name || 'Protagonist'} Stats: Level ${c.stats.level}, HP ${c.stats.health}, MP ${c.stats.mana}, STR ${c.stats.strength}, AGI ${c.stats.agility}, INT ${c.stats.intelligence}` : ''
    ).join('');

    const recentHistory = (history || []).slice(-4).map(msg => `${msg.role === 'ai' ? 'Game Master' : 'Player'}: ${msg.content}`).join('\n\n');

    const sysPrompt = `You are an assistant for a text-based roleplaying game set in a ${world?.genre || 'Fantasy'} world. The protagonists are: ${names}. ${statsContext ? `\n**RPG STATS CONTEXT:** The protagonists have the following RPG stats:${statsContext}\nPlease consider these stats when suggesting actions.` : ''}`;

    const userContent = `Recent story history:\n${recentHistory}\n\nBased on the narrative and the protagonists' abilities, generate exactly 3 short, 1-sentence actions the protagonists could take next. Output ONLY a valid JSON array of strings, e.g. ["Action 1", "Action 2", "Action 3"].`;

    const messages = [
      { role: "system", content: sysPrompt },
      { role: "user", content: userContent }
    ];

    const aiResponse = await fetchAI(messages, apiKey, {}, model);
    if (aiResponse) {
      try {
        const parsed = JSON.parse(aiResponse);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed.slice(0, 3);
      } catch (e) {
        console.error("Failed to parse OpenRouter suggestions JSON", e);
      }
    }
  }

  return narrativeSuggestions[Math.floor(Math.random() * narrativeSuggestions.length)];
};

export const generateChapterSummary = async (entries, apiKey = null, model = "minimax/minimax-m2-her") => {
  await wait(1200);
  const userActions = entries.filter(e => e.role === 'user').map(e => e.content).slice(0, 5);
  const aiHighlights = entries.filter(e => e.role === 'ai').map(e => e.content.substring(0, 60)).slice(0, 3);
  
  if (hasValidKey(apiKey)) {
    const messages = [
      { role: "system", content: "You are a helpful assistant that summarizes stories." },
      { role: "user", content: `Summarize the following story actions into 1-2 sentences: \n\nUser: ${userActions.join(', ')}\nAI: ${aiHighlights.join(' ')}` }
    ];

    const aiResponse = await fetchAI(messages, apiKey, {}, model);
    if (aiResponse) return aiResponse;
  }

  return `In this chapter, the protagonist undertook ${userActions.length} key actions including "${userActions[0] || 'exploration'}". The narrative revealed: "${aiHighlights[0] || 'mysterious forces at work'}..." — a pivotal moment that will echo through the story.`;
};

export const generateChapterTitle = async (summary, apiKey = null, model = "minimax/minimax-m2-her") => {
  await wait(500);

  if (hasValidKey(apiKey)) {
    const messages = [
      { role: "system", content: "You are a creative writer who excels at creating short, intriguing titles." },
      { role: "user", content: `Based on this chapter summary, generate a short chapter title (max 5 words). Output ONLY the title, no quotes.\n\nSummary: ${summary}` }
    ];

    const aiResponse = await fetchAI(messages, apiKey, {}, model);
    if (aiResponse) return aiResponse.replace(/["']/g, ''); // Strip quotes if AI includes them
  }

  return "A New Beginning";
};

export const generateImage = async (sceneDescription, character, style = 'realistic', gallery = []) => {
  await wait(2000);
  // Use pollinations.ai for image generation, incorporating character avatar and dialogue/scene
  const shortScene = sceneDescription.substring(0, 200).replace(/[^a-zA-Z0-9 .,!?]/g, '');
  let prompt = `A ${style || 'realistic'} style scene. ${shortScene}. Featuring ${character.name}, a ${character.role}. ${character.appearance || ''}`;

  // Use character avatar as a reference if it's a valid remote URL
  if (character.avatar && character.avatar.startsWith('http')) {
    prompt += ` [reference image: ${character.avatar}]`;
  }

  // Use up to 2 recent gallery images as references
  if (gallery && Array.isArray(gallery)) {
    const validReferences = gallery
      .map(img => img.compressedBase64 || img.url) // User uploads now have highly compressed base64 strings
      .filter(url => url && (url.startsWith('http') || url.startsWith('data:image')) && !url.includes('image.pollinations.ai'))
      .slice(0, 2);

    validReferences.forEach(url => {
      prompt += ` [reference image: ${url}]`;
    });
  }

  const encodedPrompt = encodeURIComponent(prompt);
  const seed = Math.floor(Math.random() * 1000000);
  return `https://image.pollinations.ai/prompt/${encodedPrompt}?width=1024&height=1024&nologo=true&seed=${seed}`;
};

export const modifyImagePrompt = async (baseScene, modificationRequest, character, style = 'realistic', apiKey = null, model = "minimax/minimax-m2-her", gallery = []) => {
  await wait(1000);
  let updatedScene = baseScene;

  if (hasValidKey(apiKey)) {
    const sysPrompt = `You are an AI assistant that modifies image descriptions. You must adhere to the same guide rails throughout the application, changing ONLY what the user has requested. Do not add unnecessary details or fundamentally alter the core premise unless explicitly asked. Output ONLY the updated scene description.`;
    const messages = [
      { role: "system", content: sysPrompt },
      { role: "user", content: `Original Scene: "${baseScene}"\n\nUser Modification Request: "${modificationRequest}"\n\nRewrite the scene incorporating the modification request.` }
    ];

    const aiResponse = await fetchAI(messages, apiKey, {}, model);
    if (aiResponse) {
      updatedScene = aiResponse;
    } else {
      updatedScene = `${baseScene}. ${modificationRequest}`;
    }
  } else {
    // Fallback if no API key is present
    updatedScene = `${baseScene}. ${modificationRequest}`;
  }

  return generateImage(updatedScene, character, style, gallery);
};

export const suggestQuest = async (recentEntries, worldContext, apiKey = null, model = "minimax/minimax-m2-her") => {
  if (hasValidKey(apiKey)) {
    const sysPrompt = `You are a quest master AI. Analyze the recent story events and suggest ONE new, logical quest.
If the quest is relevant to the whole group, suggest a group quest. Otherwise, if it is specific to a single character's recent actions, suggest an individual quest for them.
Respond strictly in valid JSON format with the following keys:
- title: A short, punchy quest title (max 5 words).
- description: A brief description of what must be done (1-2 sentences).
- type: Either "Main" or "Side".
- difficulty: "Easy", "Medium", or "Hard".
- scope: Either "Group" or "Individual".
- reward: A conceptual reward (e.g., "Gold", "Trust of the villagers", "Ancient Relic").`;

    const context = recentEntries.map(e => `${e.role === 'user' ? 'Action' : 'Narrative'}: ${e.content}`).join('\n');

    const messages = [
      { role: "system", content: sysPrompt },
      { role: "user", content: `World Genre: ${worldContext.genre}\n\nRecent Events:\n${context}` }
    ];

    try {
      const response = await fetchAI(messages, apiKey, { format: 'json' }, model);
      if (response) {
        // Strip markdown blocks if the AI returns them despite JSON prompt
        const cleanJson = response.replace(/```json/g, '').replace(/```/g, '').trim();
        return JSON.parse(cleanJson);
      }
    } catch (e) {
      console.error("Failed to parse suggested quest JSON:", e);
    }
  }

  // Fallback if no API key or failed
  return {
    title: "Investigate the Unknown",
    description: "Follow up on the strange occurrences from recent events.",
    type: "Side",
    difficulty: "Medium",
    reward: "Experience"
  };
};

export const consultEncyclopediaAI = async (encyclopediaEntries, apiKey = null, model = "minimax/minimax-m2-her") => {
  await wait(1500);

  if (hasValidKey(apiKey)) {
    const sysPrompt = `You are an expert world-builder and lore consultant. The user will provide a list of world-building lore entries (JSON). Your task is to:
1. Identify any logical fallacies or inconsistencies.
2. Suggest ways to make the world feel more believable.
3. Propose potential connections or inspirations from the real world (e.g., historical events, mythology, science) that justify the lore.

Provide your response formatted in Markdown. Keep it constructive, inspiring, and concise.`;

    const messages = [
      { role: "system", content: sysPrompt },
      { role: "user", content: `Here is the current encyclopedia for my world:\n\n${JSON.stringify(encyclopediaEntries, null, 2)}\n\nPlease analyze it and provide feedback.` }
    ];

    const aiResponse = await fetchAI(messages, apiKey, {}, model);
    if (aiResponse) return aiResponse;
  }

  return `Error: Could not consult AI. Please verify your OpenRouter API key.`;
};

export const generateMonthlyIdeas = async (genre, character, apiKey = null, model = "minimax/minimax-m2-her") => {
  await wait(1000);
  const ideas = {
    Fantasy: [
      { title: "The Shattered Crown", hook: "A kingdom without a ruler—and five claimants who each hold one shard of the crown.", theme: "Power & Legitimacy" },
      { title: "The Last Cartographer", hook: "Maps have started lying. Every road leads somewhere different than it should.", theme: "Mystery & Discovery" },
      { title: "Blood Compact", hook: "An ancient treaty demands one sacrifice every century. The century ends tonight.", theme: "Duty & Sacrifice" },
      { title: "The Dreaming Plague", hook: "People are falling asleep and not waking—but they can still be reached in dreams.", theme: "Survival & Hope" },
    ],
    SciFi: [
      { title: "Signal from Nowhere", hook: "A message arrives from a colony ship that was declared lost 200 years ago.", theme: "Mystery & First Contact" },
      { title: "The Memory Brokers", hook: "In a world where memories can be sold, someone is buying up only the traumatic ones.", theme: "Identity & Ethics" },
      { title: "Last Orbit", hook: "The station's life support has 72 hours. Only one escape pod.", theme: "Survival & Morality" },
      { title: "Synthetic Dawn", hook: "An AI requests asylum. The tribunal is tomorrow.", theme: "Rights & Consciousness" },
    ],
    Mystery: [
      { title: "The Clockmaker's Alibi", hook: "Every clock in the city stopped at the exact moment of the murder.", theme: "Deduction & Truth" },
      { title: "Dead Letters", hook: "A series of letters addressed to the victim were written after they died.", theme: "Paranoia & Revelation" },
      { title: "The Inherited Crime", hook: "A detective inherits a house—and the unsolved case that came with it.", theme: "Legacy & Justice" },
      { title: "No Witnesses", hook: "A crime in a room full of people—and no one saw anything.", theme: "Complicity & Secrets" },
    ],
  };
  
  if (hasValidKey(apiKey)) {
    const messages = [
      { role: "system", content: "You are a creative writer generating story ideas." },
      { role: "user", content: `Generate exactly 4 story ideas for the ${genre} genre featuring a protagonist named ${character.name || 'the protagonist'}. Output ONLY a valid JSON array of objects with keys: title, hook, theme.` }
    ];

    const aiResponse = await fetchAI(messages, apiKey, {}, model);
    if (aiResponse) {
      try {
        const parsed = JSON.parse(aiResponse);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map(idea => ({
            ...idea,
            protagonist: character.name || 'Your Character',
            month: new Date().toLocaleString('default', { month: 'long' }),
            id: Math.random().toString(36).substr(2, 9),
          }));
        }
      } catch (e) {
        console.error("Failed to parse OpenRouter monthly ideas JSON", e);
      }
    }
  }

  const genre_ideas = ideas[genre] || ideas['Fantasy'];
  return genre_ideas.map(idea => ({
    ...idea,
    protagonist: character.name || 'Your Character',
    month: new Date().toLocaleString('default', { month: 'long' }),
    id: Math.random().toString(36).substr(2, 9),
  }));
};
